import { Grid, Typography, Box, Stack } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { LanguageType, CampaignSnsRequirementSnsType } from '~/generated/graphql'
import { TagInput } from '../campaign_create/input_form/TagInput'
import { CampaignDesiredLanguageLabel } from '~/lib/types/Campaign'
import { SUPPORTED_TAG_LANGUAGES, getSnsTagLimit } from '~/lib/sns'
import { LightTooltip } from '~/components/common/LightTooltip'
import { colorPalette } from '~/constants/styles/colors'
import { CampaignPostFooter } from '~/components/CampaignPostFooter'

export type TagListItem = {
  languageType: LanguageType
  tags: string[]
}

export type LanguageSpecificTagsInputProps = {
  snsType: CampaignSnsRequirementSnsType
  tagList: TagListItem[]
  errorMessage?: string
  disabled?: boolean
  desiredLanguages?: LanguageType[]
  onChange: (updatedTagList: TagListItem[]) => void
  onError?: (errorMessage?: string) => void
}

export const LanguageSpecificTagsInput = ({
  snsType,
  tagList,
  errorMessage,
  disabled = false,
  desiredLanguages,
  onChange,
  onError,
}: LanguageSpecificTagsInputProps) => {
  const snsTagLimit = getSnsTagLimit(snsType)

  // desiredLanguagesとサポート言語の両方に含まれる言語のみを表示
  const displayLanguages = SUPPORTED_TAG_LANGUAGES.filter((lang) => desiredLanguages?.includes(lang))

  // 言語ごとのタグ変更ハンドラーを生成
  const createTagChangeHandler = (languageType: LanguageType) => (value: string[], error?: string) => {
    const existingEntry = tagList.find((tl) => tl.languageType === languageType)

    const updatedTagList = existingEntry
      ? tagList.map((tl) => (tl.languageType === languageType ? { languageType, tags: value } : tl))
      : [...tagList, { languageType, tags: value }]

    onChange(updatedTagList)
    if (onError) {
      onError(error)
    }
  }

  return (
    <Grid container direction="column" gap={3}>
      <Grid container direction="column" gap={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" fontWeight="bold">
            インフルエンサーの投稿に載せるハッシュタグ
          </Typography>
          {displayLanguages.length >= 2 && (
            <LightTooltip
              title={
                <Stack gap={2}>
                  <Typography variant="body2">
                    サブジャンルに基づいておすすめのハッシュタグを自動設定していますが、ご自由に編集していただけます。
                  </Typography>
                  <Stack gap={1}>
                    <Typography variant="body2">
                      また、基本的には言語ごとにハッシュタグを使用しますが、必要に応じて複数の言語のハッシュタグを併用することも可能です。
                    </Typography>
                    <Stack gap={0.5}>
                      <Typography variant="body2">例：</Typography>
                      <CampaignPostFooter>
                        <Stack gap={2}>
                          <Stack gap={0.5}>
                            <Typography variant="body2">・日本語投稿用のハッシュタグ</Typography>
                            <Typography variant="body2" color={colorPalette.text.secondary} ml={3}>
                              旅行、Japan
                            </Typography>
                          </Stack>
                          <Stack gap={0.5}>
                            <Typography variant="body2">・英語投稿用のハッシュタグ</Typography>
                            <Typography variant="body2" color={colorPalette.text.secondary} ml={3}>
                              Travel、観光
                            </Typography>
                          </Stack>
                        </Stack>
                      </CampaignPostFooter>
                    </Stack>
                  </Stack>
                </Stack>
              }
              placement="top"
            >
              <InfoOutlinedIcon sx={{ fontSize: 'inherit', color: colorPalette.other.backdrop }} />
            </LightTooltip>
          )}
        </Box>
        <Typography variant="caption">
          ハッシュタグを自由に追加・削除して編集可能です。Enterまたはスペースで{snsTagLimit}個まで追加できます。
        </Typography>
      </Grid>

      {displayLanguages.map((languageType) => {
        const currentTagList = tagList.find((tl) => tl.languageType === languageType)
        const tagCount = currentTagList?.tags.length || 0

        return (
          <Grid key={languageType} container direction="column" gap={0.5}>
            <Typography variant="body2" fontWeight="bold">
              {CampaignDesiredLanguageLabel[languageType]}投稿用のハッシュタグ ({tagCount}/{snsTagLimit})
            </Typography>
            <TagInput
              multiple
              maxLength={snsTagLimit}
              value={currentTagList?.tags || []}
              errorMessage={errorMessage}
              onChange={createTagChangeHandler(languageType)}
              disabled={disabled}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}
